import React from 'react'
import { Tooltip } from '@mui/material'
import {
  CalendarToday as CalendarIcon,
  Close as CloseIcon,
  ExpandMore as ExpandIcon,
  Search as SearchIcon,
  UnfoldLess as CollapseAllIcon,
  UnfoldMore as ExpandAllIcon,
} from '@mui/icons-material'
import { getSearchType } from '../subscriber/SubscriberRequests'
import { typeColor } from '../subscriber-v2/SubscriberRequestsV2'
import { Type } from '../../store/searcher/reducer'
import s from './UsageStatsDialogV2.module.css'

// Same data the old UsageStatsAll gets: /admin-dashboard/common-calendar for
// the selected range, one entry per day.
export interface UsageStat {
  date: string
  count: number
  users_count: number
  users: number[]
  types_present: string[]
  web_requests: number
  api_requests: number
  requests: Array<{ type: string; count: number; userId: number }>
  requests_by_user: Array<{ count: number; userId: number }>
}

type Props = {
  data: UsageStat[]
  from: string
  to: string
  onClose: () => void
}

const typeName = (type: string) => getSearchType(type as Type) || type

function fmtRange(from: string, to: string) {
  const f = new Date(from)
  const t = new Date(to)
  const days = Math.round((t.getTime() - f.getTime()) / 86400000) + 1
  const opts: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short' }
  return `${f.toLocaleDateString('en-GB', opts)} – ${t.toLocaleDateString('en-GB', { ...opts, year: 'numeric' })} · ${days} day${days === 1 ? '' : 's'}`
}

function UserLink({ id }: { id: number }) {
  return (
    <a className={s.ulink} href={`/users/subscriber_v2/${id}`} target="_blank" rel="noopener noreferrer">
      #{id}
    </a>
  )
}

function TypeChip({ type }: { type: string }) {
  return (
    <Tooltip title={type}>
      <span className={s.chip}>
        <i style={{ background: typeColor(type) }} />
        {typeName(type)}
      </span>
    </Tooltip>
  )
}

function DayTables({ requests }: { requests: UsageStat['requests'] }) {
  const [userQuery, setUserQuery] = React.useState('')
  const [typeQuery, setTypeQuery] = React.useState('')

  const byUser = React.useMemo(() => {
    const map = new Map<number, number>()
    requests.forEach((r) => map.set(r.userId, (map.get(r.userId) || 0) + r.count))
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1])
  }, [requests])

  const userRows = byUser.filter(([id]) => !userQuery || String(id).includes(userQuery.trim()))
  const q = typeQuery.trim().toLowerCase()
  const typeRows = [...requests]
    .sort((a, b) => b.count - a.count)
    .filter((r) => !q || r.type.toLowerCase().includes(q) || typeName(r.type).toLowerCase().includes(q) || String(r.userId).includes(q))

  return (
    <div className={s.tables}>
      <div className={s.tbl}>
        <div className={s.tblH}>
          <b>By user</b>
          <label className={s.search}>
            <SearchIcon sx={{ fontSize: 14 }} />
            <input placeholder="User ID" value={userQuery} onChange={(e) => setUserQuery(e.target.value)} />
          </label>
        </div>
        {userRows.length ? (
          <table>
            <thead><tr><th>User</th><th className={s.r}>Requests</th></tr></thead>
            <tbody>
              {userRows.map(([id, count]) => (
                <tr key={id}><td><UserLink id={id} /></td><td className={s.r}><b>{count}</b></td></tr>
              ))}
            </tbody>
          </table>
        ) : <div className={s.tblEmpty}>Nothing matches</div>}
      </div>

      <div className={s.tbl}>
        <div className={s.tblH}>
          <b>By type &amp; user</b>
          <label className={s.search}>
            <SearchIcon sx={{ fontSize: 14 }} />
            <input placeholder="Type or user" value={typeQuery} onChange={(e) => setTypeQuery(e.target.value)} />
          </label>
        </div>
        {typeRows.length ? (
          <table>
            <thead><tr><th>Type</th><th>User</th><th className={s.r}>Requests</th></tr></thead>
            <tbody>
              {typeRows.map((r, i) => (
                <tr key={`${r.type}-${r.userId}-${i}`}>
                  <td><TypeChip type={r.type} /></td>
                  <td><UserLink id={r.userId} /></td>
                  <td className={s.r}><b>{r.count}</b></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <div className={s.tblEmpty}>Nothing matches</div>}
      </div>
    </div>
  )
}

export function UsageStatsDialogV2({ data, from, to, onClose }: Props) {
  const [typeFilter, setTypeFilter] = React.useState('all')
  const [open, setOpen] = React.useState<Set<string>>(() => new Set(data[0] ? [data[0].date] : []))

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const allTypes = React.useMemo(
    () => Array.from(new Set(data.flatMap((d) => d.types_present || []))).sort((a, b) => typeName(a).localeCompare(typeName(b))),
    [data],
  )

  // Unlike the old popup (which only hid days without the type), the numbers
  // here are recounted from `requests` for the selected type. The API has no
  // web/API split per type, so that split is shown for "All types" only.
  const filtering = typeFilter !== 'all'
  const days = React.useMemo(
    () =>
      data
        .map((d) => {
          const requests = (d.requests || []).filter((r) => !filtering || r.type === typeFilter)
          const count = filtering ? requests.reduce((sum, r) => sum + r.count, 0) : Number(d.count) || 0
          const users = new Set(requests.map((r) => r.userId))
          if (!filtering) (d.users || []).forEach((u) => users.add(u))
          return { ...d, requests, count, userCount: users.size }
        })
        .filter((d) => d.count > 0),
    [data, filtering, typeFilter],
  )

  const total = days.reduce((sum, d) => sum + d.count, 0)
  const web = filtering ? null : days.reduce((sum, d) => sum + (Number(d.web_requests) || 0), 0)
  const api = filtering ? null : days.reduce((sum, d) => sum + (Number(d.api_requests) || 0), 0)
  const uniqueUsers = new Set(days.flatMap((d) => [...d.requests.map((r) => r.userId), ...(filtering ? [] : d.users || [])]))

  const byType = React.useMemo(() => {
    const map = new Map<string, number>()
    days.forEach((d) => d.requests.forEach((r) => map.set(r.type, (map.get(r.type) || 0) + r.count)))
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1])
  }, [days])
  const topUsers = React.useMemo(() => {
    const map = new Map<number, number>()
    days.forEach((d) => d.requests.forEach((r) => map.set(r.userId, (map.get(r.userId) || 0) + r.count)))
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]).slice(0, 8)
  }, [days])

  const allOpen = days.length > 0 && days.every((d) => open.has(d.date))
  const toggleAll = () => setOpen(allOpen ? new Set() : new Set(days.map((d) => d.date)))
  const toggle = (date: string) =>
    setOpen((prev) => {
      const next = new Set(prev)
      if (next.has(date)) next.delete(date)
      else next.add(date)
      return next
    })

  const exportCsv = () => {
    const lines = ['date,type,type_name,user_id,requests']
    days.forEach((d) => d.requests.forEach((r) => lines.push([d.date, r.type, `"${typeName(r.type).replace(/"/g, '""')}"`, r.userId, r.count].join(','))))
    const href = URL.createObjectURL(new Blob([lines.join('\n')], { type: 'text/csv' }))
    const a = document.createElement('a')
    a.href = href
    a.download = `usage_${from}_${to}${filtering ? `_${typeFilter}` : ''}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(href)
  }

  const typeMax = byType[0]?.[1] || 1
  const userMax = topUsers[0]?.[1] || 1

  return (
    <div className={s.backdrop} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className={s.modal} role="dialog" aria-modal="true" aria-label="Usage details">
        <div className={s.head}>
          <h2>Usage details</h2>
          <span className={s.range}><CalendarIcon sx={{ fontSize: 13 }} />{fmtRange(from, to)}</span>
          <span className={s.spacer} />
          <select className={s.select} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} aria-label="Filter by type">
            <option value="all">All types</option>
            {allTypes.map((t) => <option key={t} value={t}>{typeName(t)}</option>)}
          </select>
          <Tooltip title={allOpen ? 'Collapse all days' : 'Expand all days'}>
            <button className={s.iconBtn} onClick={toggleAll}>
              {allOpen ? <CollapseAllIcon sx={{ fontSize: 17 }} /> : <ExpandAllIcon sx={{ fontSize: 17 }} />}
            </button>
          </Tooltip>
          <Tooltip title="Close">
            <button className={s.iconBtn} onClick={onClose} aria-label="Close"><CloseIcon sx={{ fontSize: 17 }} /></button>
          </Tooltip>
        </div>

        <div className={s.body}>
          {days.length === 0 ? (
            <div className={s.empty}>{filtering ? 'No requests of this type in the selected range' : 'No data for the selected range'}</div>
          ) : (
            <>
              <div className={s.kpis}>
                <div className={`${s.card} ${s.kpi}`}>
                  <small>Total requests</small>
                  <b>{total.toLocaleString()}</b>
                  <span>{days.length} active day{days.length === 1 ? '' : 's'}</span>
                </div>
                <div className={`${s.card} ${s.kpi}`}>
                  <small>Unique users</small>
                  <b>{uniqueUsers.size.toLocaleString()}</b>
                  <span>{(total / Math.max(1, uniqueUsers.size)).toFixed(1)} requests / user</span>
                </div>
                <div className={`${s.card} ${s.kpi}`}>
                  <small><i style={{ background: 'var(--primary)' }} />Web</small>
                  <b>{web !== null ? web.toLocaleString() : '—'}</b>
                  <span>{web !== null && total ? `${Math.round((web / total) * 100)}% of requests` : 'not split by type'}</span>
                </div>
                <div className={`${s.card} ${s.kpi}`}>
                  <small><i style={{ background: '#7C5CFF' }} />API</small>
                  <b>{api !== null ? api.toLocaleString() : '—'}</b>
                  {web !== null && api !== null && web + api > 0 ? (
                    <div className={s.stack}>
                      <i style={{ width: `${(web / (web + api)) * 100}%`, background: 'var(--primary)' }} />
                      <i style={{ width: `${(api / (web + api)) * 100}%`, background: '#7C5CFF' }} />
                    </div>
                  ) : <span>not split by type</span>}
                </div>
              </div>

              <div className={s.two}>
                <div className={s.card}>
                  <div className={s.cardH}><h3>By search type</h3><span className={s.hint}>· whole range</span></div>
                  <div className={s.cardB}>
                    {byType.map(([type, count]) => (
                      <div key={type} className={s.brow}>
                        <span className={s.bname} title={type}>{typeName(type)}<small>{type}</small></span>
                        <div className={s.bar}><i style={{ width: `${(count / typeMax) * 100}%`, background: typeColor(type) }} /></div>
                        <span className={s.num}>{count.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className={s.card}>
                  <div className={s.cardH}><h3>Top users</h3><span className={s.hint}>· whole range</span></div>
                  <div className={s.cardB}>
                    {topUsers.map(([id, count]) => (
                      <div key={id} className={s.brow}>
                        <span className={s.bname}><UserLink id={id} /></span>
                        <div className={s.bar}><i style={{ width: `${(count / userMax) * 100}%`, background: 'var(--primary)' }} /></div>
                        <span className={s.num}>{count.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className={s.secT}>
                <h3>By day</h3>
                <span className={s.hint}>· {days.length} day{days.length === 1 ? '' : 's'} with activity</span>
              </div>
              <div className={s.days}>
                {days.map((d) => {
                  const date = new Date(d.date)
                  const isOpen = open.has(d.date)
                  const dayWeb = Number(d.web_requests) || 0
                  const dayApi = Number(d.api_requests) || 0
                  return (
                    <div key={d.date} className={`${s.card} ${s.day} ${isOpen ? s.open : ''}`}>
                      <button className={s.dayHead} onClick={() => toggle(d.date)} aria-expanded={isOpen}>
                        <div className={s.date}>
                          <b>{date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</b>
                          <span>{date.toLocaleDateString('en-GB', { weekday: 'short' })} · {d.date}</span>
                        </div>
                        <span className={s.badge}>{d.count.toLocaleString()}</span>
                        {filtering ? <span /> : (
                          <div className={s.split}>
                            <span>Web {dayWeb}</span>
                            <div className={s.stack}>
                              <i style={{ width: `${dayWeb + dayApi ? (dayWeb / (dayWeb + dayApi)) * 100 : 0}%`, background: 'var(--primary)' }} />
                              <i style={{ width: `${dayWeb + dayApi ? (dayApi / (dayWeb + dayApi)) * 100 : 0}%`, background: '#7C5CFF' }} />
                            </div>
                            <span>API {dayApi}</span>
                          </div>
                        )}
                        <span className={s.users}>{d.userCount} user{d.userCount === 1 ? '' : 's'}</span>
                        <ExpandIcon className={s.chev} sx={{ fontSize: 20 }} />
                      </button>
                      {isOpen && (
                        <div className={s.dayBody}>
                          <div className={s.chips}>
                            <span className={s.chipsLbl}>Types</span>
                            {Array.from(new Set(d.requests.map((r) => r.type))).map((t) => <TypeChip key={t} type={t} />)}
                          </div>
                          <DayTables requests={d.requests} />
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </div>

        <div className={s.foot}>
          <button className={s.btn} onClick={exportCsv} disabled={days.length === 0}>Export CSV</button>
          <button className={`${s.btn} ${s.btnPrimary}`} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  )
}
